/* eslint-disable no-underscore-dangle */
// 在此读取系统配置

import type { BrowserInitConfig } from '@apmplus/web';
import type { IAnyObject } from '@yimoka/shared';
import { isVacuous, mergeWithArrayOverride } from '@yimoka/shared';

declare global {
  interface Window {
    _qiyun_config?: IAnyObject;
  }
}

/**
 * 区分环境：
 * 本地开发 → 使用相对路径 /api 走 Vite 代理（代理目标为正式 client.qiyunedu.com）
 * 构建部署（含 aspnet.dev）→ 默认直连正式外网接口，避免同域 /api 打到不可用的 dev 网关
 * html-modes 注入的 window._qiyun_config 仍可覆盖
 */
export const CLIENT_API_BASE = import.meta.env.DEV
  ? '/api'
  : 'https://client.qiyunedu.com/api';

/**
 * 拼接客户端 API 完整 URL
 *
 * @param path - 以 `/` 开头的接口路径（如 `/shared/content/portal/article/list`）
 */
export const clientApiUrl = (path: string): string => {
  const base = CLIENT_API_BASE.replace(/\/$/, '');
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return `${base}${normalizedPath}`;
};

interface IEnv {
  /** 租户 id（平台 API 使用） */
  tenantID: string;
  /** 课程 API 专用 tenantID（int32，api.icua-cipp.cn 要求） */
  courseTenantID?: string;
  /** 配置的 API 接口 */
  confAPI: string;
  mode: string;
  debug: boolean;
  api: string;
  /** 是否加载远程语言包 */
  loadRemoteLang?: boolean;
  /**
   * 前端静态资源 CDN 根路径（团队文件 frontend/assets，按项目分子目录）
   * 访问规则：https://static.huashicloud.com/assets/{path}
   * 本项目配置目录为 assets/ka/aspnet
   */
  staticAssetsBase?: string;
  /** 对外可访问的网站根地址，二维码等跨设备入口使用它而不是本机 origin */
  publicSiteOrigin?: string;
  apm?: BrowserInitConfig;
}

// 默认的配置
const defaultEnv: IEnv = {
  // 租户 id
  tenantID: 'AjzPBYwAABA',
  api: CLIENT_API_BASE,
  confAPI: CLIENT_API_BASE,
  mode: '',
  debug: true,
  /** 默认拉取远程语言包；aspnet.dev 未下发 index-dev.html 时也要直连正式 aspnet-i18n */
  loadRemoteLang: true,
  /** 团队文件 CDN：frontend > assets > ka > aspnet，上传 logo 等后通过此地址访问 */
  staticAssetsBase: 'https://static.huashicloud.com/assets/ka/aspnet',
  /** 正式门户域名；本地生成二维码也需要公网可访问地址，否则手机无法打开本机域名 */
  publicSiteOrigin: 'https://aspnet.icuadx.com',
  apm: {
    // aid: '',
    // token: '',
    aid: 0,
    token: '',
    // 忽略 volcvideo.com 域名的资源和请求采集
    plugins: {
      // 静态资源性能采集：忽略 volcvideo.com 及其子域名
      resource: {
        ignoreUrls: [/^https?:\/\/([^/]*\.)?volcvideo\.com/i],
      },
      // XHR 采集：忽略该域名
      ajax: {
        ignoreUrls: [/^https?:\/\/([^/]*\.)?volcvideo\.com/i],
      },
      // Fetch 采集：忽略该域名
      fetch: {
        ignoreUrls: [/^https?:\/\/([^/]*\.)?volcvideo\.com/i],
      },
    },
  },
};
/** 课程 API tenantID（int32，从 .env 的 VITE_COURSE_TENANT_ID 读取） */
if (import.meta.env.VITE_COURSE_TENANT_ID) {
  defaultEnv.courseTenantID = String(import.meta.env.VITE_COURSE_TENANT_ID);
}

export const ENV = !isVacuous(window._qiyun_config)
  ? mergeWithArrayOverride(defaultEnv, window._qiyun_config)
  : defaultEnv;

/**
 * 获取团队静态资源 CDN 地址
 * 上传路径：文件管理 → 团队文件 → frontend → assets → ka → aspnet → 文件名
 * 访问地址：https://static.huashicloud.com/assets/ka/aspnet/文件名
 * @param path 相对路径，如 'unesco-logo.svg' 或 'images/logo.png'
 */
export const getStaticAssetUrl = (path: string): string => {
  const base = ENV.staticAssetsBase ?? '';
  if (!base) return path;
  const normalizedBase = base.replace(/\/$/, '');
  const normalizedPath = path.replace(/^\//, '');
  return `${normalizedBase}/${normalizedPath}`;
};

/** 判断是否为本机/环回访问，这类地址手机扫码不可达，需改走配置的公网域名 */
const isLoopbackOrigin = (origin: string): boolean => {
  try {
    const { hostname } = new URL(origin);
    return hostname === 'localhost'
      || hostname === '127.0.0.1'
      || hostname === '::1'
      || hostname.endsWith('.local');
  } catch {
    return false;
  }
};

/** 获取对外可访问的网站根地址，供二维码等跨设备访问场景使用 */
export const getPublicSiteOrigin = (): string => {
  if (typeof window !== 'undefined') {
    const { origin } = window.location;
    // 线上域名（如 aspnet.icuadx.com）直接用于二维码，避免扫到错误域名的 OSS AccessDenied
    if (origin && !isLoopbackOrigin(origin)) return origin;
    // 本地 DEV 保留当前 origin（含局域网 IP），方便真机扫码联调
    if (import.meta.env.DEV && origin) return origin;
  }
  const configured = String(ENV.publicSiteOrigin ?? '')
    .trim()
    .replace(/\/$/, '');
  if (configured) return configured;
  if (typeof window === 'undefined') return '';
  return window.location.origin;
};
